import { Component, computed, inject, input, linkedSignal, output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { FilterField } from '@core/filters/path-filter/vocabulary/vocabulary';
import {
  FILTER_OP_SHAPES,
  FilterLiteral,
  FilterOpName,
  LabelLiteral,
  LabelRange,
  LabelText,
  LiteralRange,
  OpArgument,
  RELATIVE_DATE_KEYWORDS,
  RelativeDateSpec,
  pathKey
} from '@core/filters/path-filter/wire';
import { Label, LabelValueType } from '@core/models/labels.model';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { ICONS } from '@core/services/icons/icons.service';
import { labelsOfEntity } from '@core/utils/label-scope.utils';

import { EntityAutoCompleteComponent } from '@shared/components/row-edition-form/entity-auto-complete/entity-auto-complete.component';
import { SelectItem, SelectValue } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';
import { AvailableLanguages } from '@shared/types/translation.type';

import {
  PATH_FILTER_I18N,
  closedValueItems,
  fieldLabel,
  relativeDateLabel
} from '../path-filter-labels';
import { ComposerContext } from '../path-filter.models';
import { DurationInputComponent } from './duration-input/duration-input.component';
import {
  SlotSource,
  formatLiteral,
  holeArgument,
  holeOf,
  parseLiteral,
  parseLiteralList,
  slotSource,
  slotSwitches,
  splitDuration,
  valueEditorFor
} from './value-slot';

const CUSTOM_RANGE = '__custom_range__';
/** Stands for a stored `{ within: n }` spec, which the keyword list cannot name. */
const WITHIN = '__within__';

/** The control a label value takes, from the registered label the condition names. */
type LabelValueControl = 'enum' | 'reference' | 'bool' | 'typed';

/** Any label operator's argument, possibly half-filled while it is being edited. */
interface AnyLabelArg {
  label?: string;
  value?: FilterLiteral | string | null;
  values?: FilterLiteral[];
  lo?: FilterLiteral | null;
  hi?: FilterLiteral | null;
}

/** The inputs whose typed text is parsed, and so may be refused. */
type TypedSlot = 'value' | 'lo' | 'hi' | 'list' | 'label_value' | 'label_lo' | 'label_hi';

function holdsNoValue(arg: OpArgument | null): boolean {
  if (arg === null) return true;
  if (typeof arg !== 'object' || Array.isArray(arg) || !('label' in arg)) return false;
  const { value, values } = arg as AnyLabelArg;
  return (value == null || value === '') && (values ?? []).length === 0;
}

@Component({
  selector: 'app-path-filter-value',
  imports: [
    TranslatePipe,
    SelectComponent,
    EntityAutoCompleteComponent,
    DurationInputComponent
  ],
  templateUrl: './path-filter-value.component.html',
  styles: `
    :host {
      display: block;
      min-width: 0;
    }
  `
})
export class PathFilterValueComponent {
  field = input<FilterField | undefined>(undefined);
  op = input<FilterOpName | null>(null);
  arg = input<OpArgument | null>(null);
  context = input.required<ComposerContext>();
  conditionId = input.required<string>();
  /** The entity the condition's field belongs to, whose labels the key offers; null offers all. */
  ownerEntityType = input<string | null>(null);
  /** `stacked` is the pill popover: an enumerated list becomes an inline checklist, not a nested menu. */
  layout = input<'row' | 'stacked'>('row');

  argChange = output<OpArgument | null>();
  switchOperator = output<FilterOpName>();

  protected readonly ICONS = ICONS;
  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly lang = computed(() => this.currentLang() as AvailableLanguages);

  protected readonly editor = computed(() =>
    valueEditorFor(this.field(), this.op(), this.context().tags)
  );
  /** Tag operators take bare strings, where every other choice is a tagged literal. */
  private readonly takesBareText = computed(() => {
    const op = this.op();
    return op !== null && ['text', 'texts'].includes(FILTER_OP_SHAPES[op]);
  });
  protected readonly switches = computed(() =>
    slotSwitches(this.context().mode, this.field(), this.op(), this.context().vocabulary)
  );

  /**
   * Typed text that does not parse stays on screen, flagged, instead of being wiped by the old
   * value. Any new argument, field or operator from outside clears it.
   */
  private readonly rejected = linkedSignal<unknown, Partial<Record<TypedSlot, string>>>({
    source: () => [this.arg(), this.op(), this.field()],
    computation: () => ({})
  });

  /**
   * Picking "Parameter" or "This record's …" empties the slot before a choice is made; the
   * picked source has to survive that empty argument rather than fall back to "Value".
   */
  private readonly chosenSource = linkedSignal<OpArgument | null, SlotSource>({
    source: this.arg,
    computation: (arg, previous) =>
      previous && holdsNoValue(arg) ? previous.value : slotSource(arg)
  });

  protected readonly sourceOptions = computed<{ id: SlotSource; label: string }[]>(() => {
    this.lang();
    const { parameterKind, self } = this.switches();
    const record = { record: this.context().selfLabel ?? '' };
    const t = (key: string, params?: object) =>
      this.translate.instant(`${PATH_FILTER_I18N}.value.${key}`, params) as string;
    return [
      { id: 'value' as const, label: t('value') },
      ...(parameterKind ? [{ id: 'parameter' as const, label: t('parameter') }] : []),
      ...(self
        ? [
            { id: 'self_field' as const, label: t('self_field', record) },
            { id: 'self_label' as const, label: t('self_label', record) }
          ]
        : [])
    ];
  });

  protected readonly source = computed<SlotSource>(() => {
    const chosen = this.chosenSource();
    return this.sourceOptions().some((option) => option.id === chosen) ? chosen : 'value';
  });

  protected readonly parameterItems = computed<SelectItem[]>(() => {
    const kind = this.switches().parameterKind;
    return this.context()
      .parameters.filter((parameter) => parameter.kind === kind)
      .map((parameter) => ({
        id: parameter.name,
        label: parameter.label ?? parameter.name,
        icon: ICONS.bracketsCurly,
        meta: parameter.name
      }));
  });
  protected readonly parameterName = computed(() => holeOf(this.arg()));

  protected readonly selfFieldItems = computed<SelectItem[]>(() =>
    this.context()
      .vocabulary.selfFields()
      .map((field) => ({
        id: field.key,
        label: fieldLabel(field, this.lang(), this.translate)
      }))
  );
  protected readonly selfField = computed(() => {
    const arg = this.arg() as { self?: { field?: FilterField['path'] } } | null;
    const path = arg?.self?.field;
    if (!path) return null;
    const key = pathKey(path);
    return this.selfFieldItems().find((item) => item.id === key) ?? { id: key, label: key };
  });
  protected readonly selfLabelText = computed(() => {
    const arg = this.arg() as { self?: { label?: string } } | null;
    return arg?.self?.label ?? '';
  });

  protected readonly inputType = computed(() => {
    switch (this.editor().literalKind) {
      case 'integer':
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      case 'date_time':
        return 'datetime-local';
      default:
        return 'text';
    }
  });
  protected readonly hint = computed(() => {
    this.lang();
    switch (this.editor().literalKind) {
      case 'mass':
      case 'length':
        return this.translate.instant(`${PATH_FILTER_I18N}.value.quantity_hint`) as string;
      case 'duration':
        return this.translate.instant(`${PATH_FILTER_I18N}.value.duration_hint`) as string;
      default:
        return '';
    }
  });

  /**
   * A duration bound edits as number and unit only while it is empty or readable as one; any other
   * stored text stays in the text input so it is neither hidden nor rewritten.
   */
  protected editsAsDuration(
    slot: 'value' | 'lo' | 'hi',
    literal: FilterLiteral | null | undefined
  ): boolean {
    if (this.editor().literalKind !== 'duration' || this.rejected()[slot] !== undefined) {
      return false;
    }
    if (literal == null || literal === 'null') return true;
    return 'duration' in literal && splitDuration(literal.duration) !== null;
  }

  protected durationOf(literal: FilterLiteral | null | undefined): string | null {
    return literal != null && literal !== 'null' && 'duration' in literal
      ? literal.duration
      : null;
  }

  protected readonly hasRejection = computed(() => Object.keys(this.rejected()).length > 0);

  protected readonly scalarText = computed(() =>
    formatLiteral(this.arg() as FilterLiteral | null)
  );
  protected readonly range = computed(
    () => (this.arg() as Partial<LiteralRange> | null) ?? ({} as Partial<LiteralRange>)
  );
  protected readonly listText = computed(() =>
    Array.isArray(this.arg())
      ? (this.arg() as FilterLiteral[]).map(formatLiteral).join(', ')
      : ''
  );
  protected readonly selectedValues = computed<string[]>(() => {
    const arg = this.arg();
    const items = Array.isArray(arg) ? arg : arg ? [arg] : [];
    return items
      .map((item) =>
        typeof item === 'string' ? item : formatLiteral(item as FilterLiteral)
      )
      .filter((value) => value.length > 0);
  });
  /** A stored value the list no longer offers stays listed, by its raw text, so it shows as selected. */
  protected readonly enumItems = computed<SelectItem<string>[]>(() =>
    closedValueItems(
      { enumValues: [...this.editor().values], enumLabels: this.field()?.enumLabels },
      this.selectedValues(),
      this.lang()
    )
  );
  protected readonly boolValue = computed(() => {
    const arg = this.arg() as { bool?: boolean } | null;
    return arg?.bool ?? null;
  });
  protected readonly relativeItems = computed<SelectItem[]>(() => {
    this.lang();
    const arg = this.arg();
    const offersRange = this.field()?.operators.includes('between') ?? false;
    return [
      ...(arg !== null && typeof arg === 'object' && 'within' in arg
        ? [
            {
              id: WITHIN,
              label: relativeDateLabel(arg as RelativeDateSpec, this.translate)
            }
          ]
        : []),
      ...RELATIVE_DATE_KEYWORDS.map((keyword) => ({
        id: keyword,
        label: relativeDateLabel(keyword, this.translate)
      })),
      ...(offersRange
        ? [
            {
              id: CUSTOM_RANGE,
              label: this.translate.instant(
                `${PATH_FILTER_I18N}.value.custom_range`
              ) as string
            }
          ]
        : [])
    ];
  });
  protected readonly relativeValue = computed(() => {
    const arg = this.arg();
    if (typeof arg === 'string') return arg;
    return arg !== null && typeof arg === 'object' && 'within' in arg ? WITHIN : null;
  });
  protected readonly textValue = computed(() =>
    typeof this.arg() === 'string' ? (this.arg() as string) : ''
  );
  protected readonly textsValue = computed(() =>
    Array.isArray(this.arg()) ? (this.arg() as string[]).join(', ') : ''
  );
  protected readonly labelArg = computed(() => (this.arg() as AnyLabelArg | null) ?? {});
  private readonly ownLabels = computed(() => {
    const owner = this.ownerEntityType();
    const labels = this.context().labels;
    return owner === null ? labels : labelsOfEntity(labels, owner);
  });
  /** A stored key the registry no longer lists stays offered, so it shows as picked. */
  protected readonly labelKeyItems = computed<SelectItem<string>[]>(() => {
    const keys = this.ownLabels().map((label) => label.label);
    const stored = this.labelArg().label;
    return [...keys, ...(stored && !keys.includes(stored) ? [stored] : [])].map((key) => ({
      id: key,
      label: key
    }));
  });
  private readonly registeredLabel = computed<Label | null>(() => {
    const key = this.labelArg().label;
    return this.ownLabels().find((label) => label.label === key) ?? null;
  });
  protected readonly labelValueControl = computed<LabelValueControl>(() => {
    const label = this.registeredLabel();
    const shape = this.editor().labelShape;
    if (!label || (shape !== 'label_literal' && shape !== 'label_literals')) return 'typed';
    switch (label.valueType) {
      case LabelValueType.ENUM:
        return label.enumVariants?.length ? 'enum' : 'typed';
      case LabelValueType.REFERENCE:
        return label.referencedEntityType ? 'reference' : 'typed';
      case LabelValueType.BOOL:
        return shape === 'label_literal' ? 'bool' : 'typed';
      default:
        return 'typed';
    }
  });
  protected readonly labelReferenceType = computed(
    () => this.registeredLabel()?.referencedEntityType ?? ''
  );
  protected readonly labelVariantItems = computed<SelectItem<string>[]>(() =>
    closedValueItems(
      { enumValues: this.registeredLabel()?.enumVariants ?? [] },
      this.labelValues(),
      this.lang()
    )
  );
  /** The label's stored values as text, for the controls that pick them. */
  protected readonly labelValues = computed<string[]>(() => {
    const arg = this.labelArg();
    const literals =
      this.editor().labelShape === 'label_literals'
        ? (arg.values ?? [])
        : typeof arg.value === 'object' && arg.value !== null
          ? [arg.value]
          : [];
    return literals.map(formatLiteral).filter((value) => value.length > 0);
  });
  protected readonly labelBool = computed(() => {
    const value = this.labelArg().value;
    return typeof value === 'object' && value !== null && 'bool' in value
      ? value.bool
      : null;
  });
  protected readonly labelValueText = computed(() => {
    const arg = this.labelArg();
    if (this.editor().labelShape === 'label_text')
      return typeof arg.value === 'string' ? arg.value : '';
    if (this.editor().labelShape === 'label_literals')
      return (arg.values ?? []).map(formatLiteral).join(', ');
    return typeof arg.value === 'object' ? formatLiteral(arg.value) : '';
  });

  protected rejectedText(slot: TypedSlot): string | undefined {
    return this.rejected()[slot];
  }

  protected scalarOf(literal: FilterLiteral | null | undefined): string {
    return formatLiteral(literal);
  }

  protected onSource(source: SlotSource): void {
    this.chosenSource.set(source);
    if (slotSource(this.arg()) !== source) this.clearSlot();
  }

  /** Empties the value slot; a label operator keeps its label key. */
  protected clearSlot(): void {
    this.argChange.emit(
      this.editor().kind === 'label'
        ? this.labelArgument(this.labelArg().label ?? '', {})
        : null
    );
  }

  protected onParameter(name: SelectValue): void {
    if (name === null || Array.isArray(name)) return;
    const op = this.op();
    if (!op) return;
    this.argChange.emit(
      holeArgument(
        op,
        this.editor().literalKind,
        `{{${name}}}`,
        this.labelArg().label ?? ''
      )
    );
  }

  protected newParameter(): void {
    const kind = this.switches().parameterKind;
    const op = this.op();
    if (!kind || !op) return;
    const literalKind = this.editor().literalKind;
    const label = this.labelArg().label ?? '';
    this.context().requestParameter(kind, this.conditionId(), (name) =>
      holeArgument(op, literalKind, `{{${name}}}`, label)
    );
  }

  protected onSelfField(key: SelectValue): void {
    const field = this.context()
      .vocabulary.selfFields()
      .find((candidate) => candidate.key === key);
    this.argChange.emit(field ? { self: { field: field.path } } : null);
  }

  protected onSelfLabel(raw: string): void {
    this.argChange.emit(raw.trim() ? { self: { label: raw.trim() } } : null);
  }

  /** Emits what `raw` parses to; text that does not parse is kept and flagged rather than sent. */
  private commit<T>(
    slot: TypedSlot,
    raw: string,
    parsed: T | null,
    build: (value: T | null) => OpArgument | null
  ): void {
    if (parsed === null && raw.trim() !== '') {
      this.rejected.update((current) => ({ ...current, [slot]: raw }));
      return;
    }
    this.rejected.update((current) => {
      const next = { ...current };
      delete next[slot];
      return next;
    });
    this.argChange.emit(build(parsed));
  }

  protected onScalar(raw: string): void {
    this.commit(
      'value',
      raw,
      parseLiteral(this.editor().literalKind, raw),
      (value) => value
    );
  }

  protected onDuration(slot: 'value' | 'lo' | 'hi', iso: string | null): void {
    const parsed = iso === null ? null : ({ duration: iso } as FilterLiteral);
    if (slot === 'value') {
      this.commit('value', iso ?? '', parsed, (value) => value);
      return;
    }
    this.commit(
      slot,
      iso ?? '',
      parsed,
      (value) =>
        ({
          lo: this.range().lo ?? null,
          hi: this.range().hi ?? null,
          [slot]: value
        }) as unknown as LiteralRange
    );
  }

  protected onRange(end: 'lo' | 'hi', raw: string): void {
    this.commit(
      end,
      raw,
      parseLiteral(this.editor().literalKind, raw),
      (value) =>
        ({
          lo: this.range().lo ?? null,
          hi: this.range().hi ?? null,
          [end]: value
        }) as unknown as LiteralRange
    );
  }

  protected onList(raw: string): void {
    this.commit(
      'list',
      raw,
      parseLiteralList(this.editor().literalKind, raw),
      (value) => value
    );
  }

  protected onSelect(value: SelectValue): void {
    const values =
      value === null ? [] : Array.isArray(value) ? value.map(String) : [String(value)];
    this.emitChoice(values);
  }

  protected toggleChoice(value: string): void {
    const selected = this.selectedValues();
    this.emitChoice(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value]
    );
  }

  protected onReference(value: string | string[] | null): void {
    this.emitChoice(value === null ? [] : Array.isArray(value) ? value : [value]);
  }

  private emitChoice(values: string[]): void {
    if (this.takesBareText()) {
      this.argChange.emit(this.editor().multiple ? values : (values[0] ?? null));
      return;
    }
    const kind = this.editor().literalKind === 'uid' ? 'uid' : 'string';
    const literals = values.map((value) => ({ [kind]: value }) as unknown as FilterLiteral);
    this.argChange.emit(this.editor().multiple ? literals : (literals[0] ?? null));
  }

  protected onBool(value: boolean): void {
    this.argChange.emit({ bool: value });
  }

  protected onRelative(value: SelectValue): void {
    if (value === WITHIN) return;
    if (value === CUSTOM_RANGE) {
      this.switchOperator.emit('between');
      return;
    }
    this.argChange.emit(value === null ? null : (value as RelativeDateSpec));
  }

  protected onText(raw: string): void {
    this.argChange.emit(raw);
  }

  protected onTexts(raw: string): void {
    this.argChange.emit(
      raw
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
    );
  }

  protected onLabelKey(value: SelectValue): void {
    const label = value === null ? '' : String(value);
    // A value typed for another label would not fit this one's control.
    const kept = label === this.labelArg().label ? this.labelArg() : {};
    this.argChange.emit(this.labelArgument(label, kept));
  }

  /** A picked variant, entity or flag, as the literal kind the operator serves for it. */
  protected onLabelChoice(value: SelectValue | string | string[] | boolean): void {
    const label = this.labelArg().label ?? '';
    const kinds = this.field()?.literalKinds[this.op() ?? 'label_equals'] ?? [];
    const toLiteral = (item: unknown): FilterLiteral => {
      if (typeof item === 'boolean') return { bool: item };
      const text = String(item);
      return this.labelValueControl() === 'reference' && kinds.includes('uid')
        ? { uid: text }
        : { string: text };
    };
    if (this.editor().labelShape === 'label_literals') {
      const items = value === null ? [] : Array.isArray(value) ? value : [value];
      this.argChange.emit(this.labelArgument(label, { values: items.map(toLiteral) }));
      return;
    }
    const item = Array.isArray(value) ? value[0] : value;
    this.argChange.emit(
      this.labelArgument(label, {
        value: item === null || item === undefined || item === '' ? null : toLiteral(item)
      })
    );
  }

  protected onLabelValue(raw: string): void {
    const label = this.labelArg().label ?? '';
    const kind = this.editor().literalKind;
    switch (this.editor().labelShape) {
      case 'label_text':
        this.argChange.emit(this.labelArgument(label, { value: raw }));
        return;
      case 'label_literals':
        this.commit('label_value', raw, parseLiteralList(kind, raw), (values) =>
          this.labelArgument(label, { values: values ?? [] })
        );
        return;
      default:
        this.commit('label_value', raw, parseLiteral(kind, raw), (value) =>
          this.labelArgument(label, { value })
        );
    }
  }

  protected onLabelRange(end: 'lo' | 'hi', raw: string): void {
    const current = this.labelArg();
    this.commit(
      end === 'lo' ? 'label_lo' : 'label_hi',
      raw,
      parseLiteral(this.editor().literalKind, raw),
      (value) =>
        this.labelArgument(current.label ?? '', {
          lo: current.lo ?? null,
          hi: current.hi ?? null,
          [end]: value
        })
    );
  }

  /**
   * A label argument of the editor's shape with every key present: the draft's completeness
   * check reads `values.length` and `value.length` without guarding against a missing key.
   */
  private labelArgument(label: string, slot: AnyLabelArg): OpArgument {
    switch (this.editor().labelShape) {
      case 'label_literals':
        return { label, values: slot.values ?? [] };
      case 'label_range':
        return { label, lo: slot.lo ?? null, hi: slot.hi ?? null } as unknown as LabelRange;
      case 'label_text':
        return {
          label,
          value: typeof slot.value === 'string' ? slot.value : ''
        } as LabelText;
      default:
        return {
          label,
          value: typeof slot.value === 'object' ? slot.value : null
        } as unknown as LabelLiteral;
    }
  }
}
